/**
 * House of Dusk · header (lot 2). Contrat : 00_AI_BRAIN/06_RELEVE_THEME/lots/lot2_bandeau_header.md.
 * <hod-header> (sections/hod-header.liquid) :
 * - panneau « Diffusers » desktop (Q51) : ouvert au survol de la souris, au focus clavier et au
 *   premier toucher (le second suit le lien) ; ferme par Echap, un clic dehors, la sortie du
 *   pointeur ou du focus. Le lien reste un lien (All diffusers).
 * - panneau du desktop replie (750 a 899 px) : sa bascule est [data-hod-toggle] de hod-ui.js ;
 *   ferme ici quand le tiroir panier s'ouvre (01 §2, openCart) et quand la plage change.
 * - feuille mobile (<hod-sheet> de hod-ui.js) : fermee quand on quitte le mobile.
 * - aria-expanded du bouton panier suivi sur theme-drawer:open / theme-drawer:close.
 * Deux points de rupture seulement : (max-width: 899px) et (max-width: 749px).
 * Aucune transition, aucun import : module autonome.
 */
const COLLAPSED_QUERY = '(max-width: 899px)';
const MOBILE_QUERY = '(max-width: 749px)';
const LEAVE_DELAY_MS = 120;

class HodHeader extends HTMLElement {
  #collapsed = window.matchMedia(COLLAPSED_QUERY);
  #mobile = window.matchMedia(MOBILE_QUERY);
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Map<HTMLElement, number>} */
  #timers = new Map();
  /** @type {WeakSet<HTMLElement>} panneaux fermes par Echap : le focus rendu au lien ne les rouvre pas */
  #escaped = new WeakSet();
  /** @type {{ item: HTMLElement, wasOpen: boolean } | null} */
  #touch = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    for (const item of this.#items()) {
      item.addEventListener('pointerenter', this.#onEnter, { signal });
      item.addEventListener('pointerleave', this.#onLeave, { signal });
      item.addEventListener('focusin', this.#onFocusIn, { signal });
      item.addEventListener('focusout', this.#onFocusOut, { signal });
      item.addEventListener('keydown', this.#onKeydown, { signal });
      const link = this.#link(item);
      if (link) {
        link.addEventListener('pointerdown', this.#onPointerDown, { signal });
        link.addEventListener('click', this.#onLinkClick, { signal });
      }
    }

    document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
    document.addEventListener('theme-drawer:open', this.#onDrawerOpen, { signal });
    document.addEventListener('theme-drawer:close', this.#onDrawerClose, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    this.#collapsed.addEventListener('change', this.#onRangeChange, { signal });
    this.#mobile.addEventListener('change', this.#onRangeChange, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = null;
    for (const timer of this.#timers.values()) window.clearTimeout(timer);
    this.#timers.clear();
    this.#touch = null;
  }

  /* ------------------------------------------------------------------------ */
  /* Panneau « Diffusers » (desktop)                                          */
  /* ------------------------------------------------------------------------ */

  /** @returns {HTMLElement[]} */
  #items() {
    return Array.from(this.querySelectorAll('[data-hod-drop]')).filter(
      (el) => el instanceof HTMLElement
    );
  }

  /** @param {HTMLElement} item */
  #link(item) {
    const link = item.querySelector(':scope > a');
    return link instanceof HTMLElement ? link : null;
  }

  /** @param {HTMLElement} item */
  #panel(item) {
    const panel = item.querySelector(':scope > .hod-hdr__drop');
    return panel instanceof HTMLElement ? panel : null;
  }

  /** @param {HTMLElement} item */
  #isOpen(item) {
    const panel = this.#panel(item);
    return Boolean(panel && !panel.hidden);
  }

  /** @param {HTMLElement} item */
  #open(item) {
    if (this.#collapsed.matches) return;
    this.#clearTimer(item);
    for (const other of this.#items()) {
      if (other !== item) this.#close(other);
    }
    const panel = this.#panel(item);
    if (!panel || !panel.hidden) return;
    panel.hidden = false;
    this.#link(item)?.setAttribute('aria-expanded', 'true');
  }

  /** @param {HTMLElement} item */
  #close(item) {
    this.#clearTimer(item);
    const panel = this.#panel(item);
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    this.#link(item)?.setAttribute('aria-expanded', 'false');
  }

  #closeAll() {
    for (const item of this.#items()) this.#close(item);
  }

  /** @param {HTMLElement} item */
  #clearTimer(item) {
    const timer = this.#timers.get(item);
    if (timer) window.clearTimeout(timer);
    this.#timers.delete(item);
  }

  /** @param {PointerEvent} event */
  #onEnter = (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    this.#escaped.delete(item);
    this.#open(item);
  };

  /** @param {PointerEvent} event */
  #onLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    const active = document.activeElement;
    // un visiteur au clavier dans le panneau le garde ouvert
    if (active instanceof HTMLElement && item.contains(active) && active.matches(':focus-visible')) return;
    this.#clearTimer(item);
    this.#timers.set(
      item,
      window.setTimeout(() => {
        this.#timers.delete(item);
        this.#close(item);
      }, LEAVE_DELAY_MS)
    );
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    if (this.#escaped.has(item)) return;
    this.#open(item);
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    const next = event.relatedTarget;
    if (next instanceof Node && item.contains(next)) return;
    this.#escaped.delete(item);
    if (item.matches(':hover')) return;
    this.#close(item);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    if (!this.#isOpen(item)) return;
    this.#escaped.add(item);
    this.#close(item);
    this.#link(item)?.focus({ preventScroll: true });
  };

  /** Toucher : on note si le panneau etait deja ouvert avant que le focus ne l'ouvre. */
  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse') {
      this.#touch = null;
      return;
    }
    const item = /** @type {HTMLElement} */ (event.currentTarget).closest('[data-hod-drop]');
    if (!(item instanceof HTMLElement)) return;
    this.#touch = { item, wasOpen: this.#isOpen(item) };
  };

  /** Premier toucher : ouvre le panneau au lieu de suivre le lien ; le second suit le lien. */
  /** @param {MouseEvent} event */
  #onLinkClick = (event) => {
    const touch = this.#touch;
    this.#touch = null;
    if (!touch || this.#collapsed.matches) return;
    const item = /** @type {HTMLElement} */ (event.currentTarget).closest('[data-hod-drop]');
    if (touch.item !== item || touch.wasOpen) return;
    event.preventDefault();
    this.#escaped.delete(touch.item);
    this.#open(touch.item);
  };

  /** @param {PointerEvent} event */
  #onDocumentPointerDown = (event) => {
    const target = event.target;
    for (const item of this.#items()) {
      if (!this.#isOpen(item)) continue;
      if (target instanceof Node && item.contains(target)) continue;
      this.#close(item);
    }
  };

  /* ------------------------------------------------------------------------ */
  /* Panneau replie, feuille, panier                                          */
  /* ------------------------------------------------------------------------ */

  #closeCollapsedPanel() {
    const toggle = this.querySelector('[data-hod-panel-toggle]');
    if (!(toggle instanceof HTMLElement)) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!panel || panel.hidden) return;
    panel.hidden = true;
    toggle.setAttribute('aria-expanded', 'false');
  }

  /** @param {boolean} expanded */
  #setCartExpanded(expanded) {
    for (const trigger of this.querySelectorAll('[data-hod-cart-trigger]')) {
      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  /** Ouvrir le tiroir ferme le menu (01 §2 : openCart remet navOpen a false). */
  /** @param {Event} event */
  #onDrawerOpen = (event) => {
    this.#closeAll();
    this.#closeCollapsedPanel();
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') {
      this.#setCartExpanded(true);
    }
  };

  /** @param {Event} event */
  #onDrawerClose = (event) => {
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') {
      this.#setCartExpanded(false);
    }
  };

  /** Changement de plage : chaque couche ne vit que dans la sienne. */
  #onRangeChange = () => {
    this.#closeAll();
    if (!this.#collapsed.matches || this.#mobile.matches) this.#closeCollapsedPanel();
    if (!this.#mobile.matches) {
      const sheet = /** @type {any} */ (document.getElementById(this.dataset.sheet || ''));
      if (sheet && sheet.isOpen && typeof sheet.close === 'function') sheet.close();
    }
  };

  /** Retour arriere (bfcache) : aucun panneau ne reste ouvert sur la page restauree. */
  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (!event.persisted) return;
    this.#closeAll();
    this.#closeCollapsedPanel();
  };
}

if (!customElements.get('hod-header')) customElements.define('hod-header', HodHeader);
